import { computed, ref, shallowRef, useHost, type ComputedRef, type ToRefs, type Ref } from "vue";
import { useComponentContext } from "composables/useComponentContext";
import { useStateStore, getElementByUid } from "composables/useStateStore";
import { useSharedState } from "composables/useSharedState";
import { useCheckedFormState } from "composables/useFormAssociated";
import { useKeyboardNavigation } from "composables/useKeyboardNavigation";
import { createEventDispatcher } from "@/runtime/shared/events";
import { closestAcrossShadowBoundaries } from "@/runtime/shared/shadow-dom";
import type { RadioProps, RadioGroupProps } from "./radio.types";
import type { AcceptableValue } from "@/runtime/types/utils";
import { radioRootClasses, radioButtonClasses, radioIndicatorClasses } from "./radio.styles";

const RADIO_TYPE = "Radio";

/**
 * Registry mapping DOM elements to their Vue component UIDs and state.
 */
const radioRegistry = new WeakMap<Element, { uid: number; state: ToRefs<RadioProps> }>();

/**
 * Resolve the effective color for a radio button.
 * Handles encased/mono/solid/inherit logic.
 */
const resolveRadioColor = (isEncased: boolean | undefined, color: string | undefined, variant: string): string => {
  if (!isEncased) return color ?? "neutral";
  if (color === "mono") return "background";
  if (variant === "solid") return "inherit";
  if (!color) return "inherit";
  return color;
};

/** Look up the cached parent radio state from the registry. */
const lookupParentRadioState = (host: Element): ToRefs<RadioProps> | null => {
  const parentElement = closestAcrossShadowBoundaries(host, "e-radio");
  if (!parentElement) return null;
  return radioRegistry.get(parentElement)?.state ?? null;
};

/** Build the list of non-disabled radio items from the group element. */
const getRadioItems = (groupEl: Element | null): { element: HTMLElement; value: string | number }[] => {
  if (!groupEl) return [];
  const result: { element: HTMLElement; value: string | number }[] = [];
  for (const el of groupEl.querySelectorAll<HTMLElement>(":scope > e-radio")) {
    const data = radioRegistry.get(el);
    if (!data || data.state.disabled?.value) continue;
    const val = data.state.value?.value;
    if (val == null) continue;
    result.push({ element: el, value: val });
  }
  return result;
};

/**
 * Registers a Radio element with its component UID and reactive state.
 */
export const registerRadio = (element: Element, uid: number, state: ToRefs<RadioProps>): void => {
  radioRegistry.set(element, { uid, state });
};

export interface UseRadioOptions {
  props?: Readonly<RadioProps>;
  buttonRef?: Ref<HTMLButtonElement | null>;
}

export interface UseRadioReturn {
  // Radio context
  radioGroupUid: ComputedRef<number | undefined>;
  radioGroupProps: ComputedRef<RadioGroupProps | undefined>;
  radioGroupElement: ComputedRef<Element | null>;

  // State
  state: ToRefs<RadioProps>;

  // Computed properties
  rootClasses: ComputedRef<ReturnType<typeof radioRootClasses>>;
  buttonClasses: ComputedRef<ReturnType<typeof radioButtonClasses>>;
  finalColor: ComputedRef<string>;
  finalVariant: ComputedRef<string>;
  finalSize: ComputedRef<string>;
  finalName: ComputedRef<string | undefined>;
  finalRequired: ComputedRef<boolean>;
  ariaChecked: ComputedRef<boolean>;
  ariaDisabled: ComputedRef<boolean | undefined>;
  dataDisabled: ComputedRef<boolean | undefined>;
  dataState: ComputedRef<"checked" | "unchecked">;
  formValue: ComputedRef<AcceptableValue | undefined>;
  isChecked: ComputedRef<boolean>;

  // Indicator properties
  shouldShowIndicator: ComputedRef<boolean>;
  indicatorIcon: ComputedRef<string>;
  indicatorClasses: ComputedRef<ReturnType<typeof radioIndicatorClasses>>;

  // Refs
  buttonRef: Ref<HTMLButtonElement | null>;

  // Keyboard
  tabIndex: ComputedRef<0 | -1>;
  handleKeyDown: (event: KeyboardEvent) => void;

  // Methods
  handleClick: () => void;

  // Component wrapper
  WrapperComponent: ComputedRef<"e-card" | "div">;
}

/** Resolve radio context: parent state, group element, group uid, group props. */
const resolveRadioContext = (
  host: Element | null,
  context: ReturnType<typeof useComponentContext>,
  props: Readonly<RadioProps> | undefined,
  state: ToRefs<RadioProps>,
  stateStore: ReturnType<typeof useStateStore>,
) => {
  const cachedRadioState = shallowRef<ToRefs<RadioProps> | null>(null);

  if (host && context.typeName !== RADIO_TYPE) {
    cachedRadioState.value = lookupParentRadioState(host);
  }

  const radioState = computed(() => (context.typeName === RADIO_TYPE && props ? state : cachedRadioState.value));

  const radioGroupUid = computed(() => {
    if (context.uid == null) return;
    return stateStore.findParentOfType(context.uid, "RadioGroup")?.uid ?? undefined;
  });
  const radioGroupElement = computed(() => {
    const uid = radioGroupUid.value;
    if (uid == null) return null;
    return (getElementByUid(uid) as Element) ?? null;
  });
  const radioGroupProps = computed(() => {
    const uid = radioGroupUid.value;
    if (uid == null) return;
    return stateStore.getByUid(uid)?.props as RadioGroupProps | undefined;
  });

  return { radioState, radioGroupElement, radioGroupUid, radioGroupProps };
};

/** Build keyboard navigation for radio. */
const buildRadioKeyboard = (
  radioGroupElement: ComputedRef<Element | null>,
  radioState: ComputedRef<ToRefs<RadioProps> | null>,
  handleClick: () => void,
) => {
  const { handleKeydown: navKeydown } = useKeyboardNavigation({
    getItemValues: () => getRadioItems(radioGroupElement.value).map((i) => i.value),
    getHighlighted: () => radioState.value?.value?.value,
    setHighlighted: (value) => {
      const target = getRadioItems(radioGroupElement.value).find((i) => i.value === value);
      if (!target) return;
      target.element.focus();
      const groupEl = radioGroupElement.value;
      if (groupEl) createEventDispatcher(groupEl)("update:modelValue", value);
    },
    scrollIntoView: () => {},
    onSelect: () => handleClick(),
    selectKeys: [" ", "Enter"],
  });

  return (event: KeyboardEvent) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      navKeydown(
        new KeyboardEvent(event.type, {
          key: event.key === "ArrowRight" ? "ArrowDown" : "ArrowUp",
          bubbles: event.bubbles,
          cancelable: event.cancelable,
        }),
      );
      return;
    }
    navKeydown(event);
  };
};

/**
 * tabIndex follows the WAI-ARIA radio group "roving tabindex" pattern: the
 * checked radio is tab-stop; if none is checked, the first item is.
 */
const buildRadioTabIndex = (
  context: { typeName: string | undefined },
  isChecked: ComputedRef<boolean>,
  radioGroupElement: ComputedRef<Element | null> | Ref<Element | null>,
  radioGroupProps: ComputedRef<RadioGroupProps | undefined> | Ref<RadioGroupProps | undefined>,
  host: HTMLElement | null,
): ComputedRef<0 | -1> =>
  computed<0 | -1>(() => {
    if (context.typeName !== RADIO_TYPE) return -1;
    if (isChecked.value) return 0;
    const items = getRadioItems(radioGroupElement.value);
    const groupModelValue = radioGroupProps.value?.modelValue;
    const hasChecked = groupModelValue != null && items.some((i) => i.value === groupModelValue);
    return !hasChecked && items[0]?.element === host ? 0 : -1;
  });

export const useRadio = (options: UseRadioOptions = {}): UseRadioReturn => {
  const { props, buttonRef: externalButtonRef } = options;
  const stateStore = useStateStore();
  const context = useComponentContext();
  const host = useHost();
  const buttonRef = externalButtonRef ?? ref<HTMLButtonElement | null>(null);
  const state = props ? useSharedState(props) : ({} as ToRefs<RadioProps>);
  const dispatchEvent = createEventDispatcher(host);

  if (props && host && context.typeName === RADIO_TYPE && context.uid != null) {
    registerRadio(host, context.uid, state);
  }

  const ctx = resolveRadioContext(host, context, props, state, stateStore);
  const cp = buildRadioComputedProps(ctx.radioState, ctx.radioGroupProps, stateStore, ctx.radioGroupUid);
  const { ariaChecked, ariaDisabled, dataDisabled, dataState } = useCheckedFormState(cp.isChecked, cp.isDisabled);

  const handleClick = buildHandleClick(
    cp.isDisabled,
    host,
    ctx.radioGroupUid,
    ctx.radioState,
    ctx.radioGroupElement,
    dispatchEvent,
    buttonRef,
  );

  if (host) (host as HTMLElement).focus = () => buttonRef.value?.focus();

  const tabIndex = buildRadioTabIndex(context, cp.isChecked, ctx.radioGroupElement, ctx.radioGroupProps, host);

  const handleKeyDown = buildRadioKeyboard(ctx.radioGroupElement, ctx.radioState, handleClick);

  const WrapperComponent = computed(() => (ctx.radioState.value?.encased?.value ? "e-card" : "div"));

  return {
    radioGroupUid: ctx.radioGroupUid,
    radioGroupProps: ctx.radioGroupProps,
    radioGroupElement: ctx.radioGroupElement,
    state: ctx.radioState.value ?? ({} as ToRefs<RadioProps>),
    ...cp,
    ariaChecked,
    ariaDisabled,
    dataDisabled,
    dataState,
    buttonRef,
    tabIndex,
    handleKeyDown,
    handleClick,
    WrapperComponent,
  };
};

/** Build radio visual computed properties (classes, indicator). */
const buildRadioVisualProps = (
  radioState: ComputedRef<ToRefs<RadioProps> | null>,
  isDisabled: ComputedRef<boolean>,
  isChecked: ComputedRef<boolean>,
  finalColor: ComputedRef<string>,
  finalSize: ComputedRef<string>,
) => {
  const rootClasses = computed(() =>
    radioRootClasses({ disabled: isDisabled.value, encased: !!radioState.value?.encased?.value }),
  );
  const buttonClasses = computed(() =>
    radioButtonClasses({
      color: finalColor.value as NonNullable<Parameters<typeof radioButtonClasses>[0]>["color"],
      size: finalSize.value as NonNullable<Parameters<typeof radioButtonClasses>[0]>["size"],
      disabled: isDisabled.value,
      checked: isChecked.value,
    }),
  );
  const formValue = computed(() => (isChecked.value ? radioState.value?.value?.value : undefined));
  const shouldShowIndicator = computed(() => isChecked.value);
  const indicatorIcon = computed(() => radioState.value?.icon?.value ?? "circle");
  const indicatorClasses = computed(() =>
    radioIndicatorClasses({ visible: shouldShowIndicator.value ? "true" : "false" }),
  );
  return { rootClasses, buttonClasses, formValue, shouldShowIndicator, indicatorIcon, indicatorClasses };
};

/** Resolve a radio/group property with fallback. */
const resolveRadioInherited = (
  state: ToRefs<RadioProps> | null,
  gp: RadioGroupProps | undefined,
  prop: string,
  fallback?: string,
): string | undefined => {
  return (state as any)?.[prop]?.value ?? (gp as any)?.[prop] ?? fallback;
};

/** Determine if radio is checked by comparing value against group model. */
const computeRadioChecked = (
  state: ToRefs<RadioProps> | null,
  stateStore: ReturnType<typeof useStateStore>,
  uid: number | undefined,
): boolean => {
  if (!state) return false;
  const radioValue = state.value?.value;
  if (radioValue == null || uid == null) return false;
  return (stateStore.getByUid(uid)?.props as RadioGroupProps | undefined)?.modelValue === radioValue;
};

/** Resolve final color accounting for encased/mono/solid. */
const computeRadioFinalColor = (state: ToRefs<RadioProps> | null, gp: RadioGroupProps | undefined): string => {
  const color = resolveRadioInherited(state, gp, "color");
  const variant = resolveRadioInherited(state, gp, "variant", "subtle")!;
  return resolveRadioColor(state?.encased?.value, color, variant);
};

/** Build all derived computed properties for a radio. */
const buildRadioComputedProps = (
  radioState: ComputedRef<ToRefs<RadioProps> | null>,
  radioGroupProps: ComputedRef<RadioGroupProps | undefined>,
  stateStore: ReturnType<typeof useStateStore>,
  radioGroupUid: ComputedRef<number | undefined>,
) => {
  const isDisabled = computed(() => {
    const s = radioState.value;
    return s ? (s.disabled?.value ?? false) || (radioGroupProps.value?.disabled ?? false) : false;
  });
  const isChecked = computed(() => computeRadioChecked(radioState.value, stateStore, radioGroupUid.value));
  const finalColor = computed(() => computeRadioFinalColor(radioState.value, radioGroupProps.value));
  const finalVariant = computed(
    () => resolveRadioInherited(radioState.value, radioGroupProps.value, "variant", "subtle")!,
  );
  const finalSize = computed(() => resolveRadioInherited(radioState.value, radioGroupProps.value, "size", "md")!);
  const finalName = computed(() => resolveRadioInherited(radioState.value, radioGroupProps.value, "name"));
  const finalRequired = computed(() => radioState.value?.required?.value ?? radioGroupProps.value?.required ?? false);
  const visual = buildRadioVisualProps(radioState, isDisabled, isChecked, finalColor, finalSize);
  return { isDisabled, isChecked, finalColor, finalVariant, finalSize, finalName, finalRequired, ...visual };
};

/** Build the click handler for radio selection. */
const buildHandleClick = (
  isDisabled: ComputedRef<boolean>,
  host: Element | null,
  radioGroupUid: ComputedRef<number | undefined>,
  radioState: ComputedRef<ToRefs<RadioProps> | null>,
  radioGroupElement: ComputedRef<Element | null>,
  dispatchEvent: ReturnType<typeof createEventDispatcher>,
  buttonRef: Ref<HTMLButtonElement | null>,
) => {
  return () => {
    if (isDisabled.value || !host) return;
    const groupUid = radioGroupUid.value;
    if (groupUid == null) {
      buttonRef.value?.focus();
      return;
    }

    const s = radioState.value;
    if (!s) return;
    const radioValue = s.value?.value;
    if (radioValue != null) {
      const groupEl = radioGroupElement.value;
      if (groupEl) createEventDispatcher(groupEl)("update:modelValue", radioValue);
      dispatchEvent("change", radioValue);
    }
    buttonRef.value?.focus();
  };
};
